import type { TaskStatus } from '@nedelnik/shared';

/**
 * Каким статусом задача была до закрытия — чтобы снятая галочка вернула её
 * туда же, а не всегда в бэклог (3.1.9).
 *
 * Память устройства, а не поле задачи: так решил владелец 2026-10-11. Цена
 * известна — на другом устройстве, после очистки браузера или для задачи,
 * закрытой агентом, снятая галочка вернёт задачу в бэклог, как раньше.
 * Синхронизируемое поле дало бы то же везде, но это миграция, сервер и
 * белый список ради одного удобства.
 *
 * Хранится в `localStorage` под `try/catch` по той же причине, что и тема: в
 * приватном окне обращение к нему бросает исключение.
 */

export interface ReopenMemory {
  /** Запомнить статус, из которого задачу закрыли. */
  remember(taskId: string, status: TaskStatus): void;
  /** Статус до закрытия, если устройство его помнит. */
  recall(taskId: string): TaskStatus | undefined;
  forget(taskId: string): void;
}

const STORAGE_KEY = 'nedelnik.reopen';

/**
 * Сколько помнить. Задачу, закрытую два месяца назад, если и открывают, то
 * уже по другому поводу — и прежний статус тогда скорее врёт, чем помогает.
 */
const KEEP_MS = 60 * 24 * 60 * 60 * 1000;

/** Потолок записей: память не должна расти вместе с историей закрытого. */
const MAX_ENTRIES = 500;

type Stored = Record<string, [TaskStatus, number]>;

function read(): Stored {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (raw == null) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Stored) : {};
  } catch {
    return {};
  }
}

function write(stored: Stored): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Память не переживёт перезагрузку — снятая галочка вернёт в бэклог.
  }
}

export function createLocalReopenMemory(now: () => number = Date.now): ReopenMemory {
  return {
    remember(taskId, status) {
      const at = now();
      const kept = Object.entries(read())
        .filter(([id, [, stamp]]) => id !== taskId && at - stamp < KEEP_MS)
        .sort(([, [, left]], [, [, right]]) => right - left)
        .slice(0, MAX_ENTRIES - 1);
      write(Object.fromEntries([[taskId, [status, at]], ...kept]));
    },
    recall(taskId) {
      const entry = read()[taskId];
      if (entry === undefined || now() - entry[1] >= KEEP_MS) return undefined;
      return entry[0] === 'wip' || entry[0] === 'backlog' ? entry[0] : undefined;
    },
    forget(taskId) {
      const stored = read();
      if (!(taskId in stored)) return;
      delete stored[taskId];
      write(stored);
    },
  };
}
